// src/SearchableSelect.tsx
import React, { useState, useRef, useEffect } from 'react';

interface SearchableSelectProps {
  id: string;
  options: string[];
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  error?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  id,
  options,
  value,
  onChange,
  placeholder = 'Select option...',
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOpen = () => {
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      // Open upward if there is not enough room below but enough room above
      setOpenUpward(spaceBelow < 250 && rect.top > 200);
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (opt: string) => {
    onChange(opt);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        id={id}
        onClick={handleToggleOpen}
        style={{
          padding: '0.75rem 1rem',
          border: error ? '1px solid #ef4444' : isOpen ? '1px solid #3b82f6' : '1px solid #e2e8f0',
          borderRadius: '8px',
          background: '#ffffff',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.95rem',
          color: value ? '#0f172a' : '#94a3b8',
          boxShadow: isOpen ? '0 0 0 3px rgba(59, 130, 246, 0.2)' : 'none',
          transition: 'all 0.15s ease-in-out',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder}
        </span>
        <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '0.5rem' }}>
          {isOpen ? '▲' : '▼'}
        </span>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            ...(openUpward
              ? { bottom: 'calc(100% + 4px)', top: 'auto' }
              : { top: 'calc(100% + 4px)', bottom: 'auto' }),
            left: 0,
            right: 0,
            zIndex: 999999,
            background: '#ffffff',
            border: '1px solid #cbd5e1',
            borderRadius: '10px',
            boxShadow: '0 10px 25px -5px rgba(0,0,0,0.2), 0 -10px 25px -5px rgba(0,0,0,0.2)',
            maxHeight: '260px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeIn 0.15s ease-out',
          }}
        >
          {options.length > 5 && (
            <div style={{ padding: '0.5rem', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
              <input
                type="text"
                placeholder="🔍 Type to search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  fontSize: '0.875rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  outline: 'none',
                }}
              />
            </div>
          )}

          <div style={{ overflowY: 'auto', flex: 1, maxHeight: '200px' }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: '0.75rem 1rem',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    background: value === opt ? '#eff6ff' : 'transparent',
                    color: value === opt ? '#1d4ed8' : '#1e293b',
                    fontWeight: value === opt ? '600' : '400',
                    borderBottom: '1px solid #f8fafc',
                    transition: 'background 0.1s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (value !== opt) e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    if (value !== opt) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {opt}
                </div>
              ))
            ) : (
              <div style={{ padding: '1rem', fontSize: '0.875rem', color: '#94a3b8', textAlign: 'center' }}>
                No matching options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
